import { motion } from "framer-motion";
import { Plug, Radar, Send } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const FEATURES = [
  {
    number: "01",
    title: "Smart Lead Discovery",
    description: "AI scans and finds high-intent B2B prospects instantly.",
    Icon: Radar,
    glow: "group-hover:shadow-[0_0_50px_rgba(96,165,250,0.18)]",
    iconColor: "text-blue-400",
  },
  {
    number: "02",
    title: "Automated Outreach",
    description:
      "Send personalized, high-converting messages without spam filters.",
    Icon: Send,
    glow: "group-hover:shadow-[0_0_50px_rgba(34,211,238,0.18)]",
    iconColor: "text-cyan-400",
  },
  {
    number: "03",
    title: "Seamless Integration",
    description:
      "Connects directly with your favorite CRM and tools in 2 minutes.",
    Icon: Plug,
    glow: "group-hover:shadow-[0_0_50px_rgba(94,234,212,0.18)]",
    iconColor: "text-teal-400",
  },
];

export default function Features() {
  return (
    <section id="features" className="relative py-28 md:py-36" data-testid="features-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-24 h-[380px] w-[380px] rounded-full bg-cyan-700/10 blur-[120px]"
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-16 max-w-2xl"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-teal-300">
            The Playbook
          </p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Three moves.
            <br />
            One autonomous pipeline.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400 md:text-lg">
            StartX Powered runs your entire outbound motion while you sleep —
            from discovery to booked meeting.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {FEATURES.map((feature, i) => (
            <motion.div
              key={feature.number}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, delay: i * 0.14, ease: EASE }}
              whileHover={{ y: -8 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl transition-[box-shadow,border-color] duration-500 hover:border-white/20 ${feature.glow}`}
              data-testid={`feature-card-${i + 1}`}
            >
              <span
                aria-hidden="true"
                className="font-display pointer-events-none absolute -right-2 -top-6 text-[7rem] font-bold leading-none text-white/[0.04]"
              >
                {feature.number}
              </span>
              <div className="relative">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                  <feature.Icon className={`h-5 w-5 ${feature.iconColor}`} />
                </div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  {feature.number}
                </p>
                <h3 className="font-display text-xl font-semibold tracking-tight text-white">
                  {feature.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
